import { useCallback, useEffect, useState } from "react";
import {
  deleteAdminParallel,
  deleteAdminPost,
  deleteAdminUser,
  deleteAllAdminParallels,
  deleteAllAdminPosts,
  fetchAdminDataOverview,
  fetchAdminParallels,
  fetchAdminPosts,
  fetchAdminUsers,
  type AdminDataOverview,
  type AdminParallelSession,
  type AdminPost,
  type AdminUser,
} from "@/api/admin";
import { AdminCard } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/Button";

type DataSection = "users" | "frames" | "drifts" | "parallels";

type Props = {
  busy: boolean;
  onBusy: (busy: boolean, label?: string) => void;
  onToast: (message: string, kind: "success" | "error") => void;
};

const sections: { id: DataSection; label: string }[] = [
  { id: "users", label: "Users" },
  { id: "frames", label: "Frames" },
  { id: "drifts", label: "Drifts" },
  { id: "parallels", label: "Parallels" },
];

export function AdminDataTab({ busy, onBusy, onToast }: Props) {
  const [section, setSection] = useState<DataSection>("users");
  const [search, setSearch] = useState("");
  const [overview, setOverview] = useState<AdminDataOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [frames, setFrames] = useState<AdminPost[]>([]);
  const [drifts, setDrifts] = useState<AdminPost[]>([]);
  const [parallels, setParallels] = useState<AdminParallelSession[]>([]);

  const load = useCallback(async () => {
    onBusy(true, "Loading data…");
    try {
      setOverview(await fetchAdminDataOverview());
      if (section === "users") setUsers(await fetchAdminUsers(search));
      else if (section === "frames") setFrames(await fetchAdminPosts(search, { kind: "frame" }));
      else if (section === "drifts") setDrifts(await fetchAdminPosts(search, { kind: "drift" }));
      else setParallels(await fetchAdminParallels(search));
    } catch (e) {
      onToast(e instanceof Error ? e.message : "Failed to load data", "error");
    } finally {
      onBusy(false);
    }
  }, [onBusy, onToast, search, section]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runDelete(fn: () => Promise<void>, label: string, success: string) {
    onBusy(true, label);
    try {
      await fn();
      await load();
      onToast(success, "success");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "Delete failed", "error");
    } finally {
      onBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {overview ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Users" value={overview.users} />
          <StatCard label="Frames" value={overview.frames} />
          <StatCard label="Drifts" value={overview.drifts} />
          <StatCard label="Parallels" value={overview.parallels} />
          <StatCard label="Comments" value={overview.comments} />
          <StatCard label="Reactions" value={overview.reactions} />
          <StatCard label="Views / signals" value={overview.signals} />
          <StatCard label="Parallel captures" value={overview.captures} />
        </div>
      ) : null}

      <AdminCard title="Data tables">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  section === s.id
                    ? "bg-[#111] text-white"
                    : "bg-[#f3f4f6] text-ink-muted hover:bg-[#e5e7eb]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-[220px] flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
              placeholder="Search id, username, caption…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button onClick={() => void load()} disabled={busy}>
              Refresh
            </Button>
            {section === "users" ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm("Delete ALL users? This cannot be undone.")) return;
                  onToast("Bulk user delete is disabled — delete users individually.", "error");
                }}
              >
                Delete all
              </Button>
            ) : section === "frames" ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm("Delete ALL frames and attached data?")) return;
                  void (async () => {
                    onBusy(true, "Deleting all frames…");
                    try {
                      const r = await deleteAllAdminPosts("frame");
                      await load();
                      onToast(`Deleted ${r.deleted ?? 0} frames`, "success");
                    } catch (e) {
                      onToast(e instanceof Error ? e.message : "Delete failed", "error");
                    } finally {
                      onBusy(false);
                    }
                  })();
                }}
              >
                Delete all
              </Button>
            ) : section === "drifts" ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm("Delete ALL drifts and attached data?")) return;
                  void (async () => {
                    onBusy(true, "Deleting all drifts…");
                    try {
                      const r = await deleteAllAdminPosts("drift");
                      await load();
                      onToast(`Deleted ${r.deleted ?? 0} drifts`, "success");
                    } catch (e) {
                      onToast(e instanceof Error ? e.message : "Delete failed", "error");
                    } finally {
                      onBusy(false);
                    }
                  })();
                }}
              >
                Delete all
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm("Delete ALL parallels, captures, and feed posts?")) return;
                  void (async () => {
                    onBusy(true, "Deleting all parallels…");
                    try {
                      const r = await deleteAllAdminParallels();
                      await load();
                      onToast(`Deleted ${r.deleted ?? 0} sessions`, "success");
                    } catch (e) {
                      onToast(e instanceof Error ? e.message : "Delete failed", "error");
                    } finally {
                      onBusy(false);
                    }
                  })();
                }}
              >
                Delete all
              </Button>
            )}
          </div>

          {section === "users" ? (
            <div className="overflow-x-auto rounded-2xl border border-black/[0.06]">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#f8f9fb]">
                  <tr>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Content</th>
                    <th className="px-3 py-2">Activity</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-black/[0.06]">
                      <td className="px-3 py-2">
                        <div className="font-medium">@{u.username ?? "—"}</div>
                        <div className="text-xs text-ink-muted">{u.email}</div>
                        <div className="font-mono text-xs text-ink-muted">{u.id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {u.frame_count ?? 0} frames · {u.drift_count ?? 0} drifts
                        <br />
                        {u.parallel_session_count ?? 0} parallels
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {u.comment_count ?? 0} comments · {u.chat_message_count ?? 0} chats
                        <br />
                        {(u.sparks_balance ?? 0).toLocaleString()} sparks
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {u.shadow_banned ? (
                          <span className="text-red-600">Banned</span>
                        ) : (
                          <span className="text-green-700">Active</span>
                        )}
                        {u.is_admin ? <span className="ml-1 text-primary">· admin</span> : null}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          disabled={busy || u.is_admin}
                          onClick={() => {
                            if (!window.confirm(`Delete @${u.username} and ALL their app data?`)) return;
                            void runDelete(
                              () => deleteAdminUser(u.id),
                              "Deleting user…",
                              `@${u.username ?? u.id} removed`,
                            );
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {section === "frames" || section === "drifts" ? (
            <PostDataTable
              posts={section === "frames" ? frames : drifts}
              busy={busy}
              label={section === "frames" ? "frame" : "drift"}
              onDelete={(post) => {
                if (!window.confirm(`Delete this ${section === "frames" ? "frame" : "drift"} and all reactions, comments, views?`)) {
                  return;
                }
                void runDelete(
                  () => deleteAdminPost(post.id),
                  "Deleting…",
                  "Post removed with attached data",
                );
              }}
            />
          ) : null}

          {section === "parallels" ? (
            <div className="overflow-x-auto rounded-2xl border border-black/[0.06]">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#f8f9fb]">
                  <tr>
                    <th className="px-3 py-2">Session</th>
                    <th className="px-3 py-2">Members</th>
                    <th className="px-3 py-2">Captures / post</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {parallels.map((s) => (
                    <tr key={s.id} className="border-t border-black/[0.06]">
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{s.id.slice(0, 8)}…</div>
                        <div className="text-xs text-ink-muted">
                          {new Date(s.created_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">{s.member_usernames ?? "—"}</td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {s.capture_count} captures
                        <br />
                        {s.post_id ? `Post ${s.post_id.slice(0, 8)}…` : "No feed post"}
                      </td>
                      <td className="px-3 py-2 text-xs">{s.status}</td>
                      <td className="px-3 py-2">
                        <Button
                          disabled={busy}
                          onClick={() => {
                            if (!window.confirm("Delete session, captures, and linked feed posts?")) return;
                            void runDelete(
                              () => deleteAdminParallel(s.id),
                              "Deleting parallel…",
                              "Parallel session removed",
                            );
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </AdminCard>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value?: number }) {
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white px-4 py-3">
      <div className="text-xs text-ink-muted">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function PostDataTable({
  posts,
  busy,
  label,
  onDelete,
}: {
  posts: AdminPost[];
  busy: boolean;
  label: string;
  onDelete: (post: AdminPost) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-black/[0.06]">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-[#f8f9fb]">
          <tr>
            <th className="px-3 py-2">Post</th>
            <th className="px-3 py-2">Author</th>
            <th className="px-3 py-2">Engagement</th>
            <th className="px-3 py-2">Attached data</th>
            <th className="px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => (
            <tr key={p.id} className="border-t border-black/[0.06]">
              <td className="px-3 py-2">
                <div className="line-clamp-2 max-w-[200px]">{p.caption || "—"}</div>
                <div className="font-mono text-xs text-ink-muted">{p.id.slice(0, 8)}…</div>
                <div className="text-xs text-ink-muted">{new Date(p.created_at).toLocaleString()}</div>
              </td>
              <td className="px-3 py-2 text-xs">@{p.username ?? "—"}</td>
              <td className="px-3 py-2 text-xs text-ink-muted">
                {p.vibe_count ?? 0} reactions
                <br />
                {p.comment_count ?? 0} comments
                <br />
                {p.signal_count ?? 0} views
              </td>
              <td className="px-3 py-2 text-xs text-ink-muted">
                {p.bookmark_count ?? 0} bookmarks · {p.unlock_count ?? 0} unlocks
                <br />
                {p.report_count ?? 0} reports · {p.media_row_count ?? 0} media rows
              </td>
              <td className="px-3 py-2">
                <Button disabled={busy} onClick={() => onDelete(p)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {posts.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-8 text-center text-sm text-ink-muted">
                No {label}s found
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
